import React, { useState } from 'react';

import classNames from 'classnames';
import { faAsterisk } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Assignee from './assignee';
import Attachments from './attachments';
import DeleteModal from 'bundles/apex/tasks/delete_modal';
import Parent from 'bundles/apex/tasks/parent';
import Status from './status';
import Tooltip from 'components/tooltip';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { nonClosingProps } from 'bundles/apex/tasks/task_modal/helpers';

import type { Attachment, ForUser, ModalOptions, Task } from 'bundles/apex/tasks/types';
import type { Parent as ParentType } from 'bundles/crm/types';

export interface Props {
  active?: boolean;
  addTaskAttachment: (task: Task, attachment: Attachment) => void;
  attachmentsPath: string;
  currentParent?: ParentType;
  deleteTaskAttachment: any;
  features?: {
    taskAttachmentExceptions?: boolean;
  };
  forUser: ForUser;
  hideAssignee?: boolean;
  modalOpen?: boolean;
  options: ModalOptions;
  requiredTasksEnabled?: boolean;
  showParents?: boolean | 'path';
  task: Task;
  onClick?: (task: Task) => void;
  onDelete: (task: Task) => void;
  onUpdate: (task: Task) => void;
}

export default function TaskRow({
  active,
  addTaskAttachment,
  attachmentsPath,
  currentParent,
  deleteTaskAttachment,
  features,
  forUser,
  hideAssignee,
  modalOpen,
  options,
  requiredTasksEnabled,
  showParents,
  task,
  onClick,
  onDelete,
  onUpdate,
}: Props) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const disableDelete = requiredTasksEnabled && task.required &&
      !forUser.isApexAdmin && !!task.officeId && !forUser.adminedOfficeIds.includes(task.officeId);

  return <>
    <tr
      {...nonClosingProps}
      className={classNames('clickable', { 'apex-tasks-list__task--active': active })}
      onClick={onClick && (() => onClick(task))}
    >
      <td className="apex-tasks-list__status-column" style={{ width: 13 }}>
        <Status
          addTaskAttachment={addTaskAttachment}
          attachmentsPath={attachmentsPath}
          deleteTaskAttachment={deleteTaskAttachment}
          features={features}
          task={task}
          onUpdate={onUpdate}
        />
      </td>

      <td
        className="apex-tasks-list__name-column"
        style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        <b className="one-line">
          <Tooltip content={task.name} onlyOnOverflow>
            {task.name}
            {task.required &&
              <Tooltip content="Required for voucher submission">
                <FontAwesomeIcon className="ml-2 text-danger text-xs" icon={faAsterisk} />
              </Tooltip>
            }
          </Tooltip>
        </b>
      </td>

      {showParents &&
        <td style={{ maxWidth: 180 }}>
          <div className="one-line" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Parent
              asLink={showParents === 'path' && !modalOpen}
              currentParent={currentParent}
              options={options}
              task={task}
            />
          </div>
        </td>
      }
      <td>
        {(task.deadline && task.deadline.date)
          ? formatDate(task.deadline.date)
          : '--'
        }
      </td>

      {hideAssignee ? null :
        <td style={{ maxWidth: 60 }}>
          {!task.parent?.private && <Assignee assignee={task.assignee}/>}
        </td>
      }

      <td style={{ maxWidth: 120 }}>
        <Attachments task={task}/>
      </td>
    </tr>

    <DeleteModal
      show={showDeleteModal}
      onDelete={() => onDelete(task)}
      onHide={() => setShowDeleteModal(false)}
    />
  </>;
}
